import React, { useEffect, useState } from 'react';
import { useApp } from '../App';
import { apiRequest } from '../api';

const platformSignals = [
  'Find underused compute and oversized instances in minutes',
  'Detect storage tier downgrades with immediate monthly impact',
  'See reserved capacity gaps before renewal windows close',
  'Send accountable actions to owners, not just dashboards',
];

const operatingModel = [
  {
    title: 'Observe',
    description: 'Pull cloud inventory, utilization, and spend drift into one tenant view.',
  },
  {
    title: 'Decide',
    description: 'Rank recommendations by savings, risk, and confidence so teams can commit quickly.',
  },
  {
    title: 'Act',
    description: 'Track accepted and completed actions with a clear audit trail and measurable results.',
  },
];

const featureGrid = [
  {
    eyebrow: 'Cost Intelligence',
    title: 'Cut waste without hurting reliability',
    description: 'Spot low-value spend early and quantify expected savings before every change.',
  },
  {
    eyebrow: 'Capacity Decisions',
    title: 'Make rightsizing decisions faster',
    description: 'Identify where to downsize safely and where to scale before customer impact appears.',
  },
  {
    eyebrow: 'Execution Layer',
    title: 'Move from insight to owner-ready action',
    description: 'Use one workflow to review, approve, and assign optimization work across teams.',
  },
];

const faqItems = [
  {
    question: 'Will Sroik ask for administrator access to my cloud?',
    answer: 'No. Sroik is designed for read-only discovery and billing visibility. The baseline model is least privilege: billing-read access plus resource inventory visibility, with no create, update, delete, or permission-escalation actions.',
  },
  {
    question: 'What does Sroik actually read from AWS and GCP?',
    answer: 'Sroik reads billing dimensions, account and project hierarchy, and cloud resource metadata needed to build inventory, utilization, and optimization recommendations. The goal is to understand spend and infrastructure shape, not to operate workloads on your behalf.',
  },
  {
    question: 'Can Sroik change or delete resources in my environment?',
    answer: 'No. The recommended access model is read-only. Your team keeps control of execution, approval, and revocation, and Sroik stays on the observation side unless you deliberately expand scope later.',
  },
  {
    question: 'How does the lowest-friction setup work for an admin?',
    answer: 'The target onboarding flow is a single admin approval session. An AWS admin signs in once and approves a read-only cross-account role or stack. A GCP admin signs in once and grants browser, Cloud Asset viewer, and billing viewer access. After that, Sroik can continue inventory and cost syncs without repeated prompts until the access grant is revoked or expires.',
  },
  {
    question: 'Why is that safer than handing over broad credentials?',
    answer: 'Because the access boundary is explicit, auditable, and reversible. Your team grants narrowly scoped IAM permissions inside your own cloud account, can review them before approval, and can remove them at any time without affecting workloads.',
  },
  {
    question: 'What should I tell my security team?',
    answer: 'Tell them Sroik only needs read-only billing and inventory visibility, not admin access. For AWS that usually means ReadOnlyAccess plus billing-read actions. For GCP that usually means browser, Cloud Asset viewer, and billing viewer. That framing keeps the approval discussion concrete and least-privilege from the start.',
  },
];

const accessModels = [
  {
    provider: 'AWS',
    title: 'One admin approval, then ongoing read-only discovery',
    summary: 'Use a dedicated cross-account IAM role with ReadOnlyAccess plus billing-read actions. Sroik keeps scanning without repeated prompts, but cannot mutate infrastructure.',
    bullets: [
      'Admin approves a single read-only IAM role once.',
      'External ID support protects against confused-deputy risk.',
      'No create, update, delete, or privilege-escalation actions.',
    ],
  },
  {
    provider: 'GCP',
    title: 'Cloud Asset plus billing visibility, no broad project admin',
    summary: 'Use browser, Cloud Asset viewer, and billing viewer roles so Sroik can build inventory and cost visibility from a narrow, auditable boundary.',
    bullets: [
      'Admin grants IAM roles once at the right scope.',
      'Cloud Asset Inventory centralizes discovery across resources.',
      'Billing visibility stays separate from runtime mutation permissions.',
    ],
  },
];

export default function MarketingPage() {
  const { health, catalog, catalogState, dashboardPublic, currentUser } = useApp();
  const [showChatModal, setShowChatModal] = useState(false);
  const [activeLeadTab, setActiveLeadTab] = useState('chat');
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadMessage, setLeadMessage] = useState('');
  const [leadError, setLeadError] = useState('');
  const [chatTimeline, setChatTimeline] = useState([]);
  const [chatSessionActive, setChatSessionActive] = useState(false);
  const [chatAccessToken, setChatAccessToken] = useState('');
  const [chatRecoveryCode, setChatRecoveryCode] = useState('');
  const [chatRecoveryOtpSent, setChatRecoveryOtpSent] = useState(false);
  const [chatRecoveryBusy, setChatRecoveryBusy] = useState(false);
  const [chatForm, setChatForm] = useState({ name: '', email: '', company: '', message: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', subject: '', message: '' });

  const hasChatIdentity = Boolean((chatForm.name || '').trim() && (chatForm.email || '').trim());
  const showChatComposer = hasChatIdentity && (chatSessionActive || chatTimeline.length > 0 || Boolean(chatAccessToken));

  useEffect(() => {
    const email = (chatForm.email || '').trim().toLowerCase();
    if (!email) {
      setChatAccessToken('');
      return;
    }
    const storageKey = `sroik_chat_token:${email}`;
    const existingToken = window.localStorage.getItem(storageKey) || '';
    setChatAccessToken(existingToken);
    if (existingToken) {
      setChatSessionActive(true);
    }
  }, [chatForm.email]);

  useEffect(() => {
    let cancelled = false;

    async function loadChatInbox() {
      const email = (chatForm.email || '').trim();
      const token = (chatAccessToken || '').trim();
      if (!showChatModal || activeLeadTab !== 'chat' || !email || !token) return;
      try {
        const items = await apiRequest(
          `/api/v1/website/chat-inbox?email=${encodeURIComponent(email)}&access_token=${encodeURIComponent(token)}`,
        );
        if (!cancelled) {
          setChatTimeline(items || []);
        }
      } catch {
        if (!cancelled) {
          setChatTimeline([]);
        }
      }
    }

    loadChatInbox();
    const timer = setInterval(loadChatInbox, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [showChatModal, activeLeadTab, chatForm.email, chatAccessToken]);

  async function submitLead(type) {
    setLeadBusy(true);
    setLeadError('');
    setLeadMessage('');
    try {
      if (type === 'chat') {
        const email = (chatForm.email || '').trim().toLowerCase();
        const response = await apiRequest('/api/v1/website/chat-requests', {
          method: 'POST',
          body: JSON.stringify({
            ...chatForm,
            access_token: chatAccessToken || undefined,
          }),
        });
        setLeadMessage(response.message || 'Chat request submitted.');
        const issuedToken = (response.chat_access_token || '').trim();
        if (issuedToken && email) {
          setChatAccessToken(issuedToken);
          window.localStorage.setItem(`sroik_chat_token:${email}`, issuedToken);
        }
        setChatSessionActive(true);
        setChatForm((prev) => ({ ...prev, message: '' }));
      } else {
        const response = await apiRequest('/api/v1/website/contact', {
          method: 'POST',
          body: JSON.stringify(contactForm),
        });
        setLeadMessage(response.message || 'Contact request submitted.');
        setContactForm({ name: '', email: '', company: '', subject: '', message: '' });
      }
    } catch (err) {
      setLeadError(err.message || 'Could not submit request.');
    } finally {
      setLeadBusy(false);
    }
  }

  async function sendChatRecoveryOtp() {
    const email = (chatForm.email || '').trim().toLowerCase();
    if (!email) {
      setLeadError('Enter your email first to recover chat history.');
      return;
    }
    setChatRecoveryBusy(true);
    setLeadError('');
    setLeadMessage('');
    try {
      const response = await apiRequest('/api/v1/website/chat-inbox/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setChatRecoveryOtpSent(true);
      setLeadMessage(response.message || 'Verification code sent to your email address.');
    } catch (err) {
      setLeadError(err.message || 'Could not send verification code.');
    } finally {
      setChatRecoveryBusy(false);
    }
  }

  async function verifyChatRecoveryOtp() {
    const email = (chatForm.email || '').trim().toLowerCase();
    if (!email) {
      setLeadError('Enter your email first.');
      return;
    }
    if ((chatRecoveryCode || '').trim().length !== 6) {
      setLeadError('Enter the 6-digit verification code.');
      return;
    }
    setChatRecoveryBusy(true);
    setLeadError('');
    setLeadMessage('');
    try {
      const response = await apiRequest('/api/v1/website/chat-inbox/verify-otp', {
        method: 'POST',
        body: JSON.stringify({
          email,
          otp_code: chatRecoveryCode.trim(),
        }),
      });
      const issuedToken = (response.chat_access_token || '').trim();
      if (issuedToken) {
        setChatAccessToken(issuedToken);
        window.localStorage.setItem(`sroik_chat_token:${email}`, issuedToken);
      }
      setChatSessionActive(true);
      setChatRecoveryOtpSent(false);
      setChatRecoveryCode('');
      setLeadMessage(response.message || 'Legacy chat history recovered.');
    } catch (err) {
      setLeadError(err.message || 'Could not verify code.');
    } finally {
      setChatRecoveryBusy(false);
    }
  }

  return (
    <div className="app-shell">
      <div className="ambient ambient-left" />
      <div className="ambient ambient-right" />

      <header className="topbar marketing-topbar">
        <a className="brand" href="/">
          <img className="brand-mark" src="/SROIK-LOGO.PNG" alt="Sroik" />
        </a>
        <nav className="topbar-links" aria-label="Primary">
          <a href="#platform">Platform</a>
          <a href="#plans">Plans</a>
          <a href="#compliance">Compliance</a>
          <a href="#access">Access</a>
          <a href="#faq">FAQ</a>
          <a href="#workflow">Workflow</a>
          <a href="/api-reference">API Docs</a>
        </nav>
        <a className="primary-action topbar-cta" href={currentUser ? '/app/dashboard' : '/login'}>
          {currentUser ? 'Dashboard' : 'Sign in'}
        </a>
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Cloud Optimization Platform</p>
            <h1>Turn cloud waste into tracked engineering actions.</h1>
            <p className="hero-text">
              Built for VP Engineering and FinOps leads: Sroik turns usage data into
              prioritized savings actions, accountable owners, and measurable delivery.
            </p>
            <div className="hero-actions">
              <a className="primary-action" href={currentUser ? '/app/dashboard' : '/login'}>
                {currentUser ? 'Go to Workspace' : 'Open Workspace'}
              </a>
              <a className="secondary-action" href="/api-reference">Explore API</a>
            </div>
            <div className={`status-pill status-${health.state}`}>
              <span className="status-dot" />
              <span>Platform status: {health.message}</span>
            </div>
          </div>

          <aside className="hero-panel">
            <div className="panel-card panel-card-main">
              <p className="panel-label">Optimization Signals</p>
              <ul>
                {platformSignals.map((signal) => (
                  <li key={signal}>{signal}</li>
                ))}
              </ul>
            </div>

            <div className="panel-card panel-card-metric">
              <div>
                <p className="metric-value">24/7</p>
                <p className="metric-label">Visibility across cloud accounts</p>
              </div>
              <div>
                <p className="metric-value">1 API</p>
                <p className="metric-label">For auth, accounts, and recommendations</p>
              </div>
            </div>

            {(dashboardPublic || true) && (
              <div className="panel-card panel-card-graph demo-dashboard-panel">
                <div className="demo-dash-header">
                  <span className="demo-dash-label">Dashboard Preview</span>
                  <span className="demo-dash-tenant">Acme Corp · AWS + GCP</span>
                </div>

                {/* KPI strip */}
                <div className="demo-kpi-strip">
                  <div className="demo-kpi">
                    <span>Monthly Spend</span>
                    <strong>$124K</strong>
                    <em>All accounts</em>
                  </div>
                  <div className="demo-kpi demo-kpi-savings">
                    <span>Potential Savings</span>
                    <strong>$31K</strong>
                    <em className="demo-pill">25% of spend</em>
                  </div>
                  <div className="demo-kpi">
                    <span>Annualized</span>
                    <strong>$372K</strong>
                    <em>12-month value</em>
                  </div>
                  <div className="demo-kpi demo-kpi-impl">
                    <span>Implemented</span>
                    <strong>$8.4K</strong>
                    <em>Realized/mo</em>
                  </div>
                </div>

                {/* Priority actions */}
                <div className="demo-section-label">Top Priority Actions</div>
                <div className="demo-actions">
                  {[
                    { title: 'Decommission stopped EC2 i-a1b2c3d4', saving: '$5.8K', risk: 'Low' },
                    { title: 'Decommission stopped EC2 i-e5f6a7b8', saving: '$5.1K', risk: 'Low' },
                    { title: 'Downsize m5.xlarge → m5.large (p95 19%)', saving: '$3.0K', risk: 'Med' },
                  ].map((a) => (
                    <div className="demo-action-row" key={a.title}>
                      <span className="demo-action-title">{a.title}</span>
                      <span className={`demo-risk demo-risk-${a.risk.toLowerCase()}`}>{a.risk}</span>
                      <span className="demo-saving">{a.saving}</span>
                      <span className="demo-btn-accept">Accept</span>
                    </div>
                  ))}
                </div>

                {/* Account health + risk mix row */}
                <div className="demo-bottom-row">
                  <div className="demo-panel">
                    <div className="demo-section-label" style={{ marginTop: 0 }}>Account Health</div>
                    {[
                      { provider: 'AWS', id: '112233445566', spend: '$89K', savings: '$22K', pct: '25%', dot: 'red' },
                      { provider: 'GCP', id: 'acme-prod-gcp', spend: '$35K', savings: '$9K', pct: '26%', dot: 'amber' },
                    ].map((acc) => (
                      <div className="demo-account-row" key={acc.id}>
                        <span className={`demo-health-dot demo-health-${acc.dot}`} />
                        <span className="demo-provider">{acc.provider}</span>
                        <span className="demo-account-id">{acc.id}</span>
                        <span className="demo-account-spend">{acc.spend}</span>
                        <span className="demo-account-pct">{acc.pct}</span>
                      </div>
                    ))}
                  </div>
                  <div className="demo-panel">
                    <div className="demo-section-label" style={{ marginTop: 0 }}>Risk Distribution</div>
                    {[
                      { label: 'Low', w: 80, color: '#2ec6b1' },
                      { label: 'Medium', w: 14, color: '#f1c75b' },
                      { label: 'High', w: 6, color: '#ef4444' },
                    ].map((r) => (
                      <div className="demo-risk-row" key={r.label}>
                        <span>{r.label}</span>
                        <div className="demo-risk-track">
                          <div className="demo-risk-fill" style={{ width: `${r.w}%`, background: r.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="demo-dash-footer">
                  <a className="primary-action" href={currentUser ? '/app/dashboard' : '/login'} style={{ fontSize: '0.8rem', padding: '0.45rem 1rem', minHeight: 'auto' }}>
                    {currentUser ? 'Open my dashboard' : 'Sign in to see your data'}
                  </a>
                </div>
              </div>
            )}
          </aside>
        </section>

        <section className="section intro-video-section">
          <div className="intro-video-wrapper">
            <video
              className="intro-video"
              src="/Intro.mp4"
              controls
              playsInline
              preload="metadata"
              aria-label="Sroik platform introduction"
            />
          </div>
        </section>

        <section className="section section-grid" id="platform">
          <div className="section-heading">
            <p className="eyebrow">Built For Real Operations</p>
            <h2>Designed for engineering leaders who must cut spend without risking uptime.</h2>
            <p>
              Connect accounts, approve high-confidence actions, and prove execution progress
              from one API-backed workspace.
            </p>
          </div>
          <div className="feature-grid">
            {featureGrid.map((feature) => (
              <article className="feature-card" key={feature.title}>
                <p className="feature-eyebrow">{feature.eyebrow}</p>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section section-grid" id="plans">
          <div className="section-heading">
            <p className="eyebrow">Plans</p>
            <h2>Choose the rollout model your org can approve and operate.</h2>
            <p>
              Plans map to team maturity, procurement expectations, and control requirements
              without changing product behavior.
            </p>
          </div>
          <div className="feature-grid">
            {(catalog.plans || []).map((plan) => (
              <article className="feature-card plan-card" key={plan.key}>
                <p className="feature-eyebrow">{plan.audience}</p>
                <h3>{plan.name}</h3>
                <ul className="card-list">
                  {plan.features.map((feature) => (
                    <li key={feature.name}>
                      <strong>{feature.name}</strong>
                      <span>{feature.description}</span>
                    </li>
                  ))}
                </ul>
                <a className="primary-action plan-cta" href={currentUser ? '/app/dashboard' : '/login'}>
                  {currentUser ? 'Go to Dashboard' : 'Get started'}
                </a>
              </article>
            ))}
            {catalogState === 'loading' && (
              <article className="feature-card">
                <h3>Loading plans</h3>
                <p>Fetching enterprise catalog from the API.</p>
              </article>
            )}
          </div>
        </section>

        <section className="section section-grid" id="compliance">
          <div className="section-heading">
            <p className="eyebrow">Compliance And Security</p>
            <h2>SOC-minded controls today, FedRAMP-oriented posture as the enterprise track.</h2>
          </div>
          <div className="feature-grid compliance-grid">
            {(catalog.frameworks || []).map((framework) => (
              <article className="feature-card" key={framework.name}>
                <p className="feature-eyebrow">{framework.status}</p>
                <h3>{framework.name}</h3>
                <p>{framework.summary}</p>
              </article>
            ))}
            <article className="feature-card security-capabilities-card">
              <p className="feature-eyebrow">Controls</p>
              <h3>Security baseline</h3>
              <ul className="card-list compact-list">
                {(catalog.security_capabilities || []).map((capability) => (
                  <li key={capability}>{capability}</li>
                ))}
              </ul>
            </article>
          </div>
        </section>

        <section className="section access-section" id="access">
          <div className="section-heading">
            <p className="eyebrow">Access Setup</p>
            <h2>Ask for one explicit admin approval, not standing broad access.</h2>
            <p>
              Sroik is designed around the same operator constraint your customers want to hear:
              scan everything relevant, mutate nothing, and keep the approval boundary small.
            </p>
          </div>
          <div className="access-grid">
            <div className="access-provider-grid">
              {accessModels.map((model) => (
                <article className="access-card" key={model.provider}>
                  <p className="feature-eyebrow">{model.provider}</p>
                  <h3>{model.title}</h3>
                  <p>{model.summary}</p>
                  <ul className="card-list compact-list">
                    {model.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <aside className="access-card access-sidecar">
              <p className="feature-eyebrow">Why it closes faster</p>
              <h3>Security teams can review the boundary in one pass.</h3>
              <ul className="card-list compact-list">
                <li>Read-only inventory and billing visibility are separated from execution.</li>
                <li>The approval is auditable and revocable inside the customer cloud account.</li>
                <li>After approval, Sroik continues syncing without repetitive consent prompts.</li>
              </ul>
              <div className="workspace-actions">
                <a className="primary-action" href={currentUser ? '/app/accounts' : '/login'}>Open guided setup</a>
              </div>
            </aside>
          </div>
        </section>

        <section className="section workflow-section" id="workflow">
          <div className="section-heading">
            <p className="eyebrow">Operating Workflow</p>
            <h2>From cloud telemetry to completed optimization work.</h2>
            <p>
              Run a repeatable weekly operating loop shared by FinOps and engineering
              instead of ad-hoc cost reviews.
            </p>
          </div>
          <div className="workflow-grid">
            {operatingModel.map((step, index) => (
              <article className="workflow-card" key={step.title}>
                <p className="workflow-index">0{index + 1}</p>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section section-grid faq-section" id="faq">
          <div className="section-heading">
            <p className="eyebrow">FAQ</p>
            <h2>Answer the security question before it blocks the deal.</h2>
            <p>
              The fastest way to approve Sroik is to be explicit: read-only inventory,
              read-only billing, one admin approval flow, and no mutation privileges.
            </p>
          </div>
          <div className="faq-grid">
            {faqItems.map((item) => (
              <details className="faq-item" key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="section closing-section">
          <div className="closing-card">
            <div>
              <p className="eyebrow">Sroik Platform</p>
              <h2>Cloud optimization with enterprise-grade access, security, and execution workflows.</h2>
              <p className="closing-text">
                Bring your cloud admin once, approve least-privilege access once, and let Sroik handle
                the ongoing read-only discovery and billing correlation from there.
              </p>
            </div>
            <div className="closing-actions">
              <a className="primary-action" href={currentUser ? '/app/dashboard' : '/login'}>
                {currentUser ? 'Go to Workspace' : 'Open Workspace'}
              </a>
              <a className="secondary-action" href="https://api.sroik.com/docs" target="_blank" rel="noopener noreferrer">Open Swagger UI</a>
            </div>
            <div className="legal-links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms &amp; Conditions</a>
              <a href="/liability-disclaimer">Liability Disclaimer</a>
            </div>
          </div>
        </section>
      </main>

      <button
        className="chat-float-button"
        type="button"
        onClick={() => {
          setShowChatModal(true);
          setLeadError('');
          setLeadMessage('');
        }}
        aria-label="Open chat and contact"
      >
        Chat
      </button>

      {showChatModal && (
        <div className="setup-modal-overlay" role="dialog" aria-modal="true" aria-label="Website chat and contact">
          <div className="setup-modal setup-help-modal chat-modal">
            <div className="setup-modal-header">
              <h3>Talk To Sroik</h3>
              <button className="secondary-action" type="button" onClick={() => setShowChatModal(false)}>Close</button>
            </div>

            <div className="auth-tabs">
              <button
                className={activeLeadTab === 'chat' ? 'auth-tab active' : 'auth-tab'}
                type="button"
                onClick={() => setActiveLeadTab('chat')}
              >
                Chat Request
              </button>
              <button
                className={activeLeadTab === 'contact' ? 'auth-tab active' : 'auth-tab'}
                type="button"
                onClick={() => setActiveLeadTab('contact')}
              >
                Contact Us
              </button>
            </div>

            {leadMessage && <p className="auth-feedback auth-success">{leadMessage}</p>}
            {leadError && <p className="auth-feedback auth-error">{leadError}</p>}

            {activeLeadTab === 'chat' && (
              <form
                className="auth-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitLead('chat');
                }}
              >
                {!showChatComposer && (
                  <>
                    <label>
                      Name
                      <input value={chatForm.name} onChange={(e) => setChatForm({ ...chatForm, name: e.target.value })} type="text" required />
                    </label>
                    <label>
                      Email
                      <input value={chatForm.email} onChange={(e) => setChatForm({ ...chatForm, email: e.target.value })} type="email" required />
                    </label>
                    <label>
                      Company
                      <input value={chatForm.company} onChange={(e) => setChatForm({ ...chatForm, company: e.target.value })} type="text" />
                    </label>
                    <label>
                      Message
                      <textarea value={chatForm.message} onChange={(e) => setChatForm({ ...chatForm, message: e.target.value })} required />
                    </label>
                    <button className="primary-action" type="submit" disabled={leadBusy}>{leadBusy ? 'Submitting...' : 'Start Chat'}</button>
                    <div className="setup-guide">
                      <p className="form-hint">Already chatted before on this email? Recover older chat history.</p>
                      <div className="account-actions">
                        <button className="secondary-action ghost" type="button" onClick={sendChatRecoveryOtp} disabled={chatRecoveryBusy}>
                          {chatRecoveryBusy ? 'Sending...' : 'Send Verification Code'}
                        </button>
                      </div>
                      {chatRecoveryOtpSent && (
                        <>
                          <label>
                            Verification Code
                            <input
                              value={chatRecoveryCode}
                              onChange={(e) => setChatRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]{6}"
                              placeholder="6-digit code"
                              required
                            />
                          </label>
                          <div className="account-actions">
                            <button className="primary-action" type="button" onClick={verifyChatRecoveryOtp} disabled={chatRecoveryBusy}>
                              {chatRecoveryBusy ? 'Verifying...' : 'Verify And Recover'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {showChatComposer && (
                  <>
                    <div className="chat-identity-strip">
                      <span>
                        Chatting as {chatForm.name} ({chatForm.email})
                        {chatForm.company ? ` · ${chatForm.company}` : ''}
                      </span>
                      <button
                        className="secondary-action ghost"
                        type="button"
                        onClick={() => {
                          setChatSessionActive(false);
                          setChatTimeline([]);
                          const email = (chatForm.email || '').trim().toLowerCase();
                          if (email) {
                            window.localStorage.removeItem(`sroik_chat_token:${email}`);
                          }
                          setChatAccessToken('');
                          setChatRecoveryCode('');
                          setChatRecoveryOtpSent(false);
                          setChatForm({ name: '', email: '', company: '', message: '' });
                          setLeadMessage('');
                          setLeadError('');
                        }}
                      >
                        Change
                      </button>
                    </div>

                    <div className="chat-window">
                      <h4>Chat Updates</h4>
                      {chatTimeline.length === 0 && <p className="form-hint">No updates yet. Send a message to start.</p>}
                      <ul className="card-list compact-list chat-thread-list">
                        {chatTimeline.map((item) => (
                          <li key={item.id}>
                            <div>
                              <p className="form-hint"><strong>You:</strong> {item.message}</p>
                              {item.response_message && <p className="form-hint"><strong>Support:</strong> {item.response_message}</p>}
                              <span className="account-hint">Status: {item.status}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <label>
                      Message
                      <textarea value={chatForm.message} onChange={(e) => setChatForm({ ...chatForm, message: e.target.value })} required />
                    </label>
                    <button className="primary-action" type="submit" disabled={leadBusy}>{leadBusy ? 'Submitting...' : 'Send Message'}</button>
                  </>
                )}
              </form>
            )}

            {activeLeadTab === 'contact' && (
              <form
                className="auth-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitLead('contact');
                }}
              >
                <label>
                  Name
                  <input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} type="text" required />
                </label>
                <label>
                  Email
                  <input value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} type="email" required />
                </label>
                <label>
                  Company
                  <input value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} type="text" />
                </label>
                <label>
                  Subject
                  <input value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })} type="text" />
                </label>
                <label>
                  Message
                  <textarea value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} required />
                </label>
                <button className="primary-action" type="submit" disabled={leadBusy}>{leadBusy ? 'Submitting...' : 'Send Contact Message'}</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
